export type ToggleSize = 'sm' | 'md';

export interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
  size?: ToggleSize;
}

/* V2: un solo interruptor canónico — 32×18 (--evo-toggle-w/h), pad 2,
 * perilla 14. `size` se conserva por compatibilidad de API. */
const TRACK_W: Record<ToggleSize, number> = { sm: 32, md: 32 };
const TRACK_H: Record<ToggleSize, number> = { sm: 18, md: 18 };
const KNOB_SZ: Record<ToggleSize, number> = { sm: 14, md: 14 };
const KNOB_OFF: Record<ToggleSize, number> = { sm: 2,  md: 2  };

export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  size = 'md',
}: ToggleProps) {
  const trackW  = TRACK_W[size];
  const trackH  = TRACK_H[size];
  const knobSz  = KNOB_SZ[size];
  const knobOff = KNOB_OFF[size];
  const travel  = trackW - knobSz - knobOff * 2;
  const hasLabel = label != null || description != null;
  /* Cuatro estados (regla dura del handoff): un interruptor ENCENDIDO pero
   * bloqueado CONSERVA el relleno cian y solo baja la perilla a --evo-fg-3 —
   * apagar el relleno para indicar bloqueo lo hace leerse como apagado. */
  const lockedOn = disabled && checked;

  return (
    <div
      role="switch"
      aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      style={{
        display: 'inline-flex',
        alignItems: hasLabel ? 'flex-start' : 'center',
        gap: '10px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled && !checked ? 0.5 : 1,
        userSelect: 'none',
      }}
    >
      {/* Track */}
      <span
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          width: trackW,
          height: trackH,
          borderRadius: trackH,
          background: checked ? 'var(--evo-cyan)' : 'var(--evo-bg-3)',
          border: `1px solid ${checked ? 'var(--evo-cyan)' : 'var(--evo-stroke)'}`,
          transition: 'background 150ms ease, border-color 150ms ease',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
      >
        {/* Knob */}
        <span
          style={{
            position: 'absolute',
            left: checked ? knobOff + travel : knobOff,
            width: knobSz,
            height: knobSz,
            borderRadius: '50%',
            background: lockedOn ? 'var(--evo-fg-3)' : checked ? 'var(--evo-fg-on-cyan)' : 'var(--evo-fg-2)',
            transition: 'left 150ms ease, background 150ms ease',
                        flexShrink: 0,
          }}
        />
      </span>

      {/* Label + description */}
      {hasLabel && (
        <span style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginTop: '1px' }}>
          {label != null && (
            <span
              style={{
                fontSize: 'var(--evo-fs-body)',
                fontWeight: 500,
                color: 'var(--evo-fg-2)',
                fontFamily: 'var(--evo-font)',
                lineHeight: 1,
              }}
            >
              {label}
            </span>
          )}
          {description != null && (
            <span
              style={{
                fontSize: 'var(--evo-fs-caption)',
                color: 'var(--evo-fg-3)',
                fontFamily: 'var(--evo-font)',
                lineHeight: 1.4,
              }}
            >
              {description}
            </span>
          )}
        </span>
      )}
    </div>
  );
}
